import React, {ChangeEvent, useCallback, useEffect, useRef, useState} from 'react';
import {Button, message, Modal} from 'antd';
import OrderListingQueryHint from '../../../../components/filters/OrderListingQueryHint';
import dayjs from 'dayjs';

import {useParcelDownload} from "../../../../hooks/useParcelDownload";
import ParcelsDownloadModal from "./ParcelsDownloadModal";
import ShippingLabelExportSuccessModal from "./ShippingLabelExportSuccessModal";
import {MARK_AS_EXCEPTION, MarkAsExceptionInput} from '../../../../graphql/mutations/markAsException';
import {DocumentNode, useMutation, useQuery} from '@apollo/client';
import {
    BATCH_MARK_FOR_MANUAL_REVIEW,
    BatchMarkForManualReviewResponse,
    BatchMarkForManualReviewVariables
} from '../graphql/mutations/batchMarkForManualReviewMutations';
import AddToPodReviewModal from './AddToPodReviewModal';
import {getUserInfo, hasHeadquartersAccess, isHubManager, isWarehouseManager} from "../../../../utils/auth";
import {useSetDeliverStatus} from "../../../../hooks/useSetDeliverStatus";
import SetDeliverStatusModal from "../../../../components/SetDeliverStatusModal";
import FailedOrdersModal from "../../../../components/FailedOrdersModal";
import {OrderType} from "../../../../graphql/types/OrderType";
import {
    GENERATE_BATCH_SHIPPING_LABEL_PDF,
    GenerateBatchShippingLabelPdfResponse,
    GenerateBatchShippingLabelPdfVariables
} from "../../../delivery/parcels/graphql/mutations/generateShippingLabelPdf";
import { allChannelResponse, GET_ALL_CHANNEL } from '../../../../graphql/queries/getChannelAll';
import {printPdfFromBase64} from "../../../wms/bags/utils/printUtils";
import InputFilterItem from "../../../delivery/components/InputFilterItem";
import {
    DeliveredAtRangeField,
    HistoryParcelSystem,
    isDeliveredAtOutOfRange,
    useParcelQueryRangeDays,
} from "../../../../components/parcelQueryRange";
import BatchInputFilterItem from "../../../delivery/components/BatchInputFilterItem";
import ParcelFilterStyle from "../../../delivery/components/ParcelFilterStyle";
import DateTimeRangeFilterItem from "../../../delivery/components/DateTimeRangeFilterItem";
import {
    hasExceptionOptions,
    isComplaintOptions,
    overallStatusOptions,
    podReviewStatusOptions
} from "../../../../config/filterOptions";
import {OrderFiltersType} from "../../../../graphql/types/OrderFiltersType";
import {
    ExceptionDictionaryResponse,
    ExceptionDictionaryType,
    GET_ALL_EXCEPTION
} from "../../../delivery/parcels/graphql/queries/getAllException";
import {filterExceptionByUserRole} from "../../../../config/roleExceptionConfig";
import SetReturnStatusModal, {SetReturnStatusData} from "../../../../components/SetReturnStatusModal";
import {
    GET_NEXT_ACTION_LIST,
    NextActionListResponse,
    NextActionListType
} from "../../../../graphql/queries/getNextActionList";
import {SET_RETURNED_STATUS} from "../../../../graphql/mutations/setReturnedStatus";
import {
    GENERATE_PARCEL_DOWNLOAD_REPORT,
    GenerateParcelDownloadReportResponse,
    GenerateParcelDownloadReportVariables
} from "../graphql/mutations/generateParcelDownloadReport";
import {
    GENERATE_ORDER_HISTORY_DOWNLOAD_REPORT,
    GenerateOrderHistoryDownloadReportResponse,
    GenerateOrderHistoryDownloadReportVariables
} from "../graphql/mutations/generateOrderHistoryDownloadReport";
import {
    GENERATE_PARCEL_SHIPPING_LABEL_DOWNLOAD_REPORT,
    GenerateParcelShippingLabelDownloadReportResponse,
    GenerateParcelShippingLabelDownloadReportVariables
} from '../graphql/mutations/generateParcelShippingLabelDownloadReport';
import {formatDateTimeRangeToISO, getLastWeekDateTimeRange} from "../../../../utils/dateTimeUtils";
import { parseException, showError } from '../../../../utils/exceptionUtils';
import {
    GetHubListForOptionResponse,
    GET_HUB_LIST_FOR_OPTIONS
} from "../../../../graphql/queries/warehouseQueries";
import BatchMarkAsExceptionModal, { BatchMarkExceptionInput } from 'components/BatchMarkAsExceptionModal';
import { GET_WAREHOUSES_FOR_CENTRAL, WarehousesResponse } from '../../exceptions/graphql/queries/getWarehouseForCentral';
import { useTranslation } from 'react-i18next';
import { useStatusInternationalization } from 'pages/delivery/parcels/hooks/useStatusInternationalization';
import { useWarehouseContext } from '../../../../contexts/WarehouseContext';

import { useDspOptions } from '../../../../hooks/useDspOptions';
import { HubSelectFilter } from '../../../../components/filters/HubSelectFilter';
import { WarehouseSelectFilter } from '../../../../components/filters/WarehouseSelectFilter';
import { DspSelectFilter } from '../../../../components/filters/DspSelectFilter';
import { MultiClientSelectFilter } from '../../../../components/filters/MultiClientSelectFilter';
import { MultiPickupHubSelectFilter } from '../../../../components/filters/MultiPickupHubSelectFilter';
import { PrePickupHubSelectFilter } from '../../../../components/filters/PrePickupHubSelectFilter';
import { MultiParcelStatusSelectFilter } from '../../../../components/filters/MultiParcelStatusSelectFilter';
import BagTaskNumberFilterItem from './BagTaskNumberFilterItem';
import { getSmartLabelClassName } from '../../../../utils/labelWidthUtils';
import { useAutoClearDefaultTime } from '../../../../hooks/useAutoClearDefaultTime';

const PARCEL_SHIPPING_LABEL_EXPORT_MAX = 10_000;

interface CentralParcelFilterProps {
    filters: OrderFiltersType;
    setFilters: (filters: OrderFiltersType | ((prev: OrderFiltersType) => OrderFiltersType)) => void;
    handleFilter: () => void;
    handleResetFilters: () => void;
    selectedRows: OrderType[];
    /** 当前查询总条数（与列表 total 一致），用于面单导出上限与空数据判断 */
    queryTotalElements: number;
    onClearSelection?: () => void; // 添加清空选择的回调函数
    /**
     * 隐藏「异常信息」整组筛选（是否有异常 / 上次异常原因 / 上次异常时间 /
     * 异常状态 / 下一步动作）。历史包裹查询页用 true。
     *
     * 这 5 个界面项对应 7 个筛选字段，其中只有异常状态来自 exception_order 表；
     * 其余来自 order 表、数据在历史索引里也有。整组隐藏是产品决策，
     * 要恢复的话把这个 prop 传 false 即可，后端与索引都无需改动。
     */
    hideExceptionFilters?: boolean;
    /**
     * 隐藏左侧批量操作按钮组（打印面单 / 导出面单 / 标记异常 / 设置退件状态 /
     * 添加到POD审核 / 设置妥投状态）。历史包裹查询页用 true。
     *
     * 归档订单只读，不提供任何写操作入口。右侧的复制运单号 / 下载 / 查询 / 重置
     * 均为只读操作，不受此开关影响，历史页同样保留。默认 false，原「包裹查询」页零影响。
     */
    hideActionButtons?: boolean;
    /**
     * 「下载」按钮走历史数据源（generateOrderHistoryDownloadReport，查 order_listing_history 索引）。
     * 历史包裹查询页用 true；默认 false 走实时 generateParcelDownloadReport，原「包裹查询」页零影响。
     * 两个 mutation 签名一致（filters + timezone → fileName），仅数据源不同。
     */
    isHistoryDownload?: boolean;
    /**
     * 历史下载按系统切换后端入口（WMS / 客服，PRD R6），返回字段需别名为 generateOrderHistoryDownloadReport；
     * 不传走总部 generateOrderHistoryDownloadReport。
     */
    historyDownloadMutation?: DocumentNode;
    /** 历史下载附加的固定筛选条件（WMS：当前仓库 currentWarehouseId / sortingCode），覆盖页面筛选 */
    historyDownloadExtraFilters?: Record<string, any>;
    /** 历史下载附加的 mutation 变量（WMS：siteType） */
    historyDownloadExtraVariables?: Record<string, any>;
    /**
     * 包裹查询页传所属系统，开启「妥投后 N 天」查询范围：签收时间说明 + 跳转该系统的历史包裹查询、
     * 签收时间限制（开始、结束都超范围时禁止查询）。历史包裹查询页不传。
     */
    queryRangeSystem?: HistoryParcelSystem;
}

interface CustomerOption {
    value: string;
    label: string;
}

const CentralParcelFilter: React.FC<CentralParcelFilterProps> = ({
                                                       filters,
                                                       setFilters,
                                                       handleFilter,
                                                       handleResetFilters,
                                                       selectedRows,
                                                       queryTotalElements,
                                                       onClearSelection,
                                                       hideExceptionFilters = false,
                                                       hideActionButtons = false,
                                                       isHistoryDownload = false,
                                                       historyDownloadMutation,
                                                       historyDownloadExtraFilters,
                                                       historyDownloadExtraVariables,
                                                       queryRangeSystem,
                                                   }) => {
    const {t, i18n} = useTranslation();
    //签收时间开始、结束都超出「妥投后 N 天」时禁止查询（按钮置灰，级联选择触发的查询同样拦截）
    const queryRangeDays = useParcelQueryRangeDays();
    const deliveredAtBlocked = !!queryRangeSystem && isDeliveredAtOutOfRange(filters.deliveredAt, queryRangeDays);
    const guardedHandleFilter = () => {
        if (deliveredAtBlocked) return;
        handleFilter();
    };
    const currentLanguage = (i18n.resolvedLanguage || i18n.language || 'en').split('-')[0];
    const isSpanish = currentLanguage === 'es';

    // ===== Label constants (for column alignment) =====
    const labelTracking = String(t('central.parcels.filters.tracking'));
    const labelOrder = String(t('central.parcels.filters.order'));
    const labelExternalTracking = String(t('central.parcels.filters.externalTrackingNumber'));
    const labelClient = String(t('central.parcels.filters.client'));
    const labelParcelStatus = String(t('central.parcels.filters.parcelStatus'));
    const labelDsp = 'DSP';

    const labelSortCode1 = String(t('central.parcels.filters.sortCode1'));
    const labelSortCode2 = String(t('central.parcels.filters.sortCode2'));
    const labelSortCode3 = String(t('central.parcels.filters.sortCode3'));
    const labelReceiverZipcode = String(t('central.parcels.filters.receiverZipcode'));
    const labelBagTaskNumber = String(t('central.parcels.filters.bagTaskNumber'));
    const labelSortingChannelOrderNumber = String(t('central.parcels.filters.sortingChannelOrderNumber'));
    const labelPrePickupHub = String(t('central.parcels.filters.prePickupHub'));
    const labelPickupHub = String(t('delivery.parcels.label.pickupHub'));
    const labelPickupSite = String(t('central.parcels.filters.pickupSite'));

    const labelOrderTime = String(t('central.parcels.filters.orderTime'));
    const labelPickupTaskDate = String(t('central.parcels.filters.pickupTaskDate'));
    const labelDeliveredAt = String(t('central.parcels.filters.deliveredAt'));
    const labelHubReceiveDate = String(t('central.parcels.filters.hubReceiveDate'));
    const labelWhsReceiveDate = String(t('central.parcels.filters.whsReceiveDate'));
    const labelLastDeliveryScanTime = String(t('central.parcels.filters.lastDeliveryScanTime'));
    const labelLastTaskDate = String(t('central.parcels.filters.lastTaskDate'));
    const labelLastUpdatedAt = String(t('delivery.parcels.label.lastUpdatedAt'));
    const labelReturnArrivalHubAt = String(t('central.parcels.filters.returnArrivalHubAt'));

    // 默认时间范围不写入 URL：用于 UI 默认展示与下载参数构造
    // - undefined：代表“未初始化”，默认展示最近一周
    // - null：代表用户手动清空（点击叉号），必须保持为空，不再回填默认值
    // 一旦用户动过时间（包含点叉号清空），在 reset/刷新前都不再自动清空默认时间
    const hasUserEditedOrderTimeRef = useRef(false);
    useAutoClearDefaultTime({
        filters,
        setFilters: (next) => setFilters(next),
        timeKey: 'orderTime',
        hasUserEditedTimeRef: hasUserEditedOrderTimeRef,
    });

    const getEffectiveOrderTime = useCallback((
        orderTime: OrderFiltersType['orderTime']
    ): [string | null, string | null] | null => {
        if (orderTime === null) return null;
        const isValid = orderTime &&
            Array.isArray(orderTime) &&
            orderTime.length === 2;
        return isValid ? (orderTime as [string | null, string | null]) : getLastWeekDateTimeRange();
    }, []);

    const labelIsComplaint = String(t('central.parcels.filters.isComplaint'));
    const labelPodReviewStatus = String(t('delivery.parcels.label.podReviewStatus'));
    const labelHasException = String(t('central.parcels.filters.hasException'));
    const labelLastExceptionReason = String(t('central.parcels.filters.lastExceptionReason'));
    const labelLastExceptionTime = String(t('central.parcels.filters.lastExceptionDate'));
    const labelNextAction = String(t('central.parcels.filters.nextAction'));
    const labelExceptionStatus = String(t('central.parcels.filters.exceptionStatus'));

    // ===== Cross-block 3-column alignment =====
    const col1Labels = [
        labelTracking,
        labelOrder,
        labelDsp,
        labelSortCode1,
        labelReceiverZipcode,
        labelIsComplaint,
        labelLastExceptionReason,
    ];
    const col2Labels = [
        labelExternalTracking,
        labelClient,
        labelSortCode2,
        labelBagTaskNumber,
        labelPickupSite,
        labelPodReviewStatus,
        labelExceptionStatus,
    ];
    const col3Labels = [
        labelParcelStatus,
        labelSortCode3,
        labelPickupHub,
        labelHasException,
        labelNextAction,
        labelSortingChannelOrderNumber,
    ];

    // Date Information 区域为二分布局（两列）
    const dateCol1Labels = [
        labelOrderTime,
        labelDeliveredAt,
        labelWhsReceiveDate,
        labelLastTaskDate,
        labelReturnArrivalHubAt,
        labelLastExceptionTime,
    ];
    const dateCol2Labels = [
        labelPickupTaskDate,
        labelHubReceiveDate,
        labelLastDeliveryScanTime,
        labelLastUpdatedAt,
    ];
    // 二分布局的第一列需要与三分布局的第一列对齐：共享同一个 label 宽度计算结果
    const col1SpanClassName = getSmartLabelClassName([...col1Labels, ...dateCol1Labels]);
    const [selectedHubId, setSelectedHubId] = useState<string | undefined>(undefined);
    const [batchModalVisible, setBatchModalVisible] = useState(false);
    const [batchMarkLoading, setBatchMarkLoading] = useState(false);
    const [returnStatusVisible, setReturnStatusVisible] = useState(false);
    const [returnStatusLoading, setReturnStatusLoading] = useState(false);
    const [addToPodReviewModalVisible, setAddToPodReviewModalVisible] = useState(false);
    const [addToPodReviewResult, setAddToPodReviewResult] = useState<{
        success: boolean;
        markedCount: number;
        failedCount: number;
        failedTrackingNumbers: string[];
    } | null>(null);
    const [addToPodReviewLoading, setAddToPodReviewLoading] = useState(false);
    const [markAsException] = useMutation(MARK_AS_EXCEPTION);
    const [setReturnedStatus] = useMutation(SET_RETURNED_STATUS);
    const [generateBatchPdf] = useMutation<GenerateBatchShippingLabelPdfResponse, GenerateBatchShippingLabelPdfVariables>(GENERATE_BATCH_SHIPPING_LABEL_PDF);
    const [batchMarkForManualReview] = useMutation<BatchMarkForManualReviewResponse, BatchMarkForManualReviewVariables>(BATCH_MARK_FOR_MANUAL_REVIEW);
    const userRole = getUserInfo()?.account?.role?.name || '';
    const [channelOptions, setChannelOptions] = useState<CustomerOption[]>([]);
    const {data} = useQuery<allChannelResponse>(GET_ALL_CHANNEL);
    const {data:exceptionData} = useQuery<ExceptionDictionaryResponse>(GET_ALL_EXCEPTION);
    const exceptionList = exceptionData?.getExceptionDictionaryForHeadquarters || [];
    const [nextActionSelectData, setNextActionSelectData] = useState<NextActionListType[]>([]);
    const {data:nextActionList} = useQuery<NextActionListResponse>(GET_NEXT_ACTION_LIST);
    const { data: hubWarehouseData } = useQuery<WarehousesResponse>(GET_WAREHOUSES_FOR_CENTRAL);
    const { currentWarehouse } = useWarehouseContext();
    const siteType = currentWarehouse?.siteType;
    const exceptionSelectData: ExceptionDictionaryType[] = filterExceptionByUserRole(exceptionList, userRole, siteType);

    // 优化的onChange函数，避免不必要的重新渲染
    // 传递 warehouseId，当清空时同时清空 sortingCodeSegment2（避免状态冲突）
    const handleWarehouseIdChange = useCallback((value: string | undefined) => {
        if (!value) {
            // 清空时，同时清空 sortingCodeSegment2 和 warehouseId
            // 必须明确设置为 undefined，这样 updateHashState 会包含这些字段，encodeHash 会过滤它们，URL hash 中不会包含
            setFilters(prev => ({
                ...prev,
                warehouseId: undefined,
                sortingCodeSegment2: undefined
            }));
        } else {
            // 选择时，只更新 warehouseId，sortingCodeSegment2 由 handleSecondSegmentChange 更新
            setFilters(prev => ({ 
                ...prev, 
                warehouseId: value
            }));
        }
    }, [setFilters]);

    // 新增：处理2nd Sort Code选择时返回对应的Sorting-name
    const handleSortingNameChange = useCallback((sortingName: string | undefined) => {
        // 这里可以根据需要处理Sorting-name，比如存储到filters中或进行其他操作
        // 如果需要将Sorting-name存储到filters中，可以取消下面的注释
        // setFilters(prev => ({ ...prev, sortingName: sortingName }));
    }, [setFilters]);

    // 新增：处理2nd Sort Code选择时返回对应的第二段码
    // 注意：清空时不会调用此回调（由 handleWarehouseIdChange 处理），所以这里只处理选择操作
    const handleSecondSegmentChange = useCallback((secondSegment: string | undefined) => {
        // 只在选择时更新 sortingCodeSegment2，清空时由 handleWarehouseIdChange 统一处理
        if (secondSegment) {
            setFilters(prev => ({ ...prev, sortingCodeSegment2: secondSegment }));
        }
    }, [setFilters]);

    const [generateReportMutation] = useMutation<GenerateParcelDownloadReportResponse, GenerateParcelDownloadReportVariables>(GENERATE_PARCEL_DOWNLOAD_REPORT);
    const [generateHistoryReportMutation] = useMutation<GenerateOrderHistoryDownloadReportResponse, GenerateOrderHistoryDownloadReportVariables>(historyDownloadMutation ?? GENERATE_ORDER_HISTORY_DOWNLOAD_REPORT);
    const [generateShippingLabelDownloadMutation] = useMutation<
        GenerateParcelShippingLabelDownloadReportResponse,
        GenerateParcelShippingLabelDownloadReportVariables
    >(GENERATE_PARCEL_SHIPPING_LABEL_DOWNLOAD_REPORT);
    const [downloadLoading, setDownloadLoading] = useState(false);
    const [cooldownRemaining, setCooldownRemaining] = useState<number>(0);
    const DOWNLOAD_COOLDOWN = 5000; // 5秒冷却时间

    // 检查是否有权限操作设置送达状态功能
    const hasSetDeliverStatusPermission = hasHeadquartersAccess() || isHubManager() || isWarehouseManager();

    // 使用公共hook
    const {
        deliverStatusVisible,
        deliverStatusLoading,
        filteredDeliverRows,
        handleSetDeliverStatus,
        handleSetDeliverStatusConfirm,
        setDeliverStatusVisible,
        failedOrdersVisible,
        failedOrders,
        setFailedOrdersVisible,
        setFailedOrders
    } = useSetDeliverStatus({
        warehouseId: null, // Central页面不需要warehouseId
        handleFilter,
        onClearSelection
    });

    // 下载完成模态框状态
    const [downloadCompleteModalVisible, setDownloadCompleteModalVisible] = useState(false);
    const [generatedFileName, setGeneratedFileName] = useState<string>('');
    // 面单异步导出成功弹窗
    const [shippingLabelExportSuccessModalVisible, setShippingLabelExportSuccessModalVisible] = useState(false);
    const [shippingLabelExportFileName, setShippingLabelExportFileName] = useState<string>('');
    const [exportShippingLabelLoading, setExportShippingLabelLoading] = useState(false);

    // 旧的  使用前端下载Hook
    const {downloadLoading: originalDownloadLoading, downloadParcels} = useParcelDownload();

    // 从localStorage获取上次下载时间，页面刷新后仍然有效
    const getLastDownloadTime = (): number => {
        const stored = localStorage.getItem('parcelDownloadLastTime');
        return stored ? parseInt(stored, 10) : 0;
    };

    const setLastDownloadTime = (time: number) => {
        localStorage.setItem('parcelDownloadLastTime', time.toString());
    };

    const [hubOptions, setHubOptions] = useState<CustomerOption[]>([]);
    const {data:hubOptionsData} = useQuery<GetHubListForOptionResponse>(GET_HUB_LIST_FOR_OPTIONS);
    const { getExceptionReasonText, getNextActionTextFromBackend, exceptionOrderStatusOptions } = useStatusInternationalization();

    // 根据选择的站点（sortingCodeSegment1 和 sortingCodeSegment2）找到对应的 warehouse ID
    const selectedWarehouseId = React.useMemo(() => {
        if (!filters.sortingCodeSegment2 || !hubWarehouseData?.warehouses) {
            return undefined;
        }
        // 查找匹配的 warehouse
        const warehouse = hubWarehouseData.warehouses.find(w => {
            if (w.siteType !== 2) return false; // 只查找 warehouse 类型
            const sortingCode = (w as any).sortingCode as string | undefined;
            const segments = sortingCode?.split('-') || [];
            const firstSegment = segments[0];
            const secondSegment = segments.length >= 2 ? segments[1] : undefined;
            // 匹配第一段和第二段
            return firstSegment === filters.sortingCodeSegment1 && secondSegment === filters.sortingCodeSegment2;
        });
        return warehouse?.id ? Number(warehouse.id) : undefined;
    }, [filters.sortingCodeSegment1, filters.sortingCodeSegment2, hubWarehouseData]);

    // DSP选项
    const { dspOptions, loading: dspLoading } = useDspOptions();
    useEffect(() => {
        if (data?.getAllChannels) {
            const channels = Array.isArray(data.getAllChannels) ? data.getAllChannels : [data.getAllChannels];
            const options = channels.map(channel => ({
                value: channel.id,
                label: channel.channelName
            }));
            setChannelOptions(options);
        }
        if (hubOptionsData) {
            const hubs = Array.isArray(hubOptionsData.allOptions) ? hubOptionsData.allOptions : [hubOptionsData.allOptions];
            setHubOptions(hubs);
        }
        if (nextActionList?.getNextActionList){
            setNextActionSelectData(nextActionList.getNextActionList);
        }
    }, [data,nextActionList,hubOptionsData]);

    // 监听重置操作：当第一分拣码被清空时，清空 selectedHubId
    useEffect(() => {
        if (!filters.sortingCodeSegment1 && selectedHubId !== undefined) {
            setSelectedHubId(undefined);
        }
    }, [filters.sortingCodeSegment1, selectedHubId]);

    // 冷却时间倒计时（页面刷新后仍然有效）
    useEffect(() => {
        const checkCooldown = () => {
            const lastTime = getLastDownloadTime();
            if (lastTime > 0) {
                const currentTime = Date.now();
                const remaining = Math.max(0, DOWNLOAD_COOLDOWN - (currentTime - lastTime));
                const remainingSeconds = Math.ceil(remaining / 1000);
                setCooldownRemaining(remainingSeconds);

                if (remaining <= 0) {
                    setCooldownRemaining(0);
                    setDownloadLoading(false);
                    localStorage.removeItem('parcelDownloadLastTime'); // 清理过期数据
                }
            } else {
                setCooldownRemaining(0);
            }
        };

        // 立即检查一次（页面刷新后）
        checkCooldown();

        // 启动定时器，每秒更新一次
        const interval = setInterval(checkCooldown, 1000);

        return () => clearInterval(interval);
    }, [DOWNLOAD_COOLDOWN]);

    // 处理时间变化的函数
    const handleOrderTimeChange = (
        dates: [string | null, string | null] | null
    ) => {
        hasUserEditedOrderTimeRef.current = true;
        setFilters({...filters, orderTime: dates});
    };

    const handleResetFiltersWithDefaultTime = () => {
        hasUserEditedOrderTimeRef.current = false;
        handleResetFilters();
        if (onClearSelection) onClearSelection();
    };

    const handleHubReceiveChange = (
        dates: [string | null, string | null] | null
    ) => {
        setFilters({...filters, destHubArrivedAt: dates});
    };

    const handleWarehouseReceiveChange = (
        dates: [string | null, string | null] | null
    ) => {
        setFilters({...filters, destWarehouseArrivedAt: dates});
    };

    const handleDeliverTaskChange = (
        dates: [string | null, string | null] | null
    ) => {
        setFilters({...filters, deliverAt: dates});
    };

    const handlePickupAtChange = (
        dates: [string | null, string | null] | null
    ) => {
        setFilters({...filters, receivedAtHub: dates});
    };

    const handleExceptionTimeChange = (
        dates: [string | null, string | null] | null,
    ) => {
        setFilters({...filters, markExceptionAt: dates});
    };

    const handleDeliveredAtChange = (
        dates: [string | null, string | null] | null
    ) => {
        setFilters({...filters, deliveredAt: dates});
    };

    const handleLastDeliveryScanTimeChange = (
        dates: [string | null, string | null] | null
    ) => {
        setFilters({...filters, lastDeliveryScanTime: dates});
    };

    const handleUpdatedAtChange = (
        dates: [string | null, string | null] | null
    ) => {
        setFilters({...filters, updatedAt: dates});
    };

    const handleReturnArrivalHubAtChange = (
        dates: [string | null, string | null] | null
    ) => {
        setFilters({...filters, returnArrivalHubAt: dates});
    };

    const handleBagTaskNumberChange = (value: string | undefined) => {
        setFilters({
            ...filters,
            bagTaskNumbers: value
        });
    };

    const copyTrackingNumbers = useCallback(async () => {
        if (!selectedRows || selectedRows.length === 0) {
            message.warning(t('central.parcels.messages.selectAtLeastOneParcel'));
            return;
        }
        const trackingNumbers = selectedRows
            .map(row => row.trackingNumber?.trim())
            .filter((tracking): tracking is string => Boolean(tracking && tracking.length > 0));
        if (trackingNumbers.length === 0) {
            message.warning(t('central.parcels.messages.selectAtLeastOneParcel'));
            return;
        }
        const text = trackingNumbers.join('\n');
        try {
            if (navigator?.clipboard?.writeText) {
                await navigator.clipboard.writeText(text);
            } else {
                const textarea = document.createElement('textarea');
                textarea.value = text;
                textarea.style.position = 'fixed';
                textarea.style.opacity = '0';
                document.body.appendChild(textarea);
                textarea.focus();
                textarea.select();
                document.execCommand('copy');
                document.body.removeChild(textarea);
            }
            message.success(t('common.messages.copied'));
        } catch (error) {
            console.error('Copy tracking numbers failed:', error);
            message.error(t('common.messages.copyFailed'));
        }
    }, [selectedRows, t]);

    const handleMarkAsException = () => {
        if (selectedRows.length < 1) {
            message.warning(t('central.parcels.messages.selectAtLeastOneParcel'));
            return;
        }
        setBatchModalVisible(true);
    };

    const handlePrintLabel = async () => {
        if (selectedRows.length < 1) {
            message.warning(t('central.parcels.messages.selectAtLeastOnePackage'));
            return;
        }

        try {
            // 批量生成一个包含多页的PDF
            const orderIds = selectedRows.map(order => parseInt(order.id));
            const { data } = await generateBatchPdf({
                variables: { orderIds }
            });

            if (data?.generateBatchShippingLabelPdf) {
                // 生成包含所有选中订单号的文件名
                const trackingNumbers = selectedRows
                    .map(order => order.trackingNumber || order.id)
                    .slice(0, 3); // 只取前3个，避免文件名过长
                const filenameSuffix = trackingNumbers.length < selectedRows.length
                    ? `${trackingNumbers.join('_')}_and_${selectedRows.length - trackingNumbers.length}_more`
                    : trackingNumbers.join('_');
                const filename = `shipping_labels_${filenameSuffix}.pdf`;

                printPdfFromBase64(data.generateBatchShippingLabelPdf, filename);
                message.success(t('central.parcels.messages.generatedBatchShippingLabel', { count: selectedRows.length }));
            }
        } catch (error: any) {
            console.error('Print tag failed:', error);
            const graphQLError = error?.graphQLErrors?.[0];
            const messageKey = graphQLError?.extensions?.messageKey;

            if (messageKey === 'errors.business.fbt.sortingOnlyBatchLabelNotSupported') {
                // 针对仅分拣订单错误，格式化 trackingNumbers
                const details = graphQLError.extensions?.details;
                const trackingNumbers = details?.trackingNumbers;
                const formattedDetails = trackingNumbers && Array.isArray(trackingNumbers)
                    ? { ...details, trackingNumbers: trackingNumbers.join(', ') }
                    : details;

                // 使用格式化后的 details 进行翻译并显示
                const errorMessage = String(t(messageKey, formattedDetails));
                message.error(errorMessage);
            } else if (messageKey) {
                // 其他有 messageKey 的错误，使用 showError
                showError(error);
            } else {
                // 兜底：无 messageKey 时使用通用文案
                message.error(t('central.parcels.messages.failedToGenerateBatchLabels'));
            }
        }
    };

    const submitShippingLabelExportTask = async () => {
        const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const queryFilters = buildQueryFilters(filters);
        const response = await generateShippingLabelDownloadMutation({
            variables: {
                filters: queryFilters,
                timezone: userTimezone,
            },
        });
        const fileName = response.data?.generateParcelShippingLabelDownloadReport;
        if (fileName) {
            setShippingLabelExportFileName(fileName);
            setShippingLabelExportSuccessModalVisible(true);
            message.success(t('central.parcels.exportShippingLabel.taskSubmitted'));
            return fileName;
        }
        message.error(t('central.parcels.exportShippingLabel.submitFailed'));
        return undefined;
    };

    const handleExportShippingLabel = async () => {
        if (selectedRows.length > 0) {
            try {
                setExportShippingLabelLoading(true);
                await handlePrintLabel();
            } finally {
                setExportShippingLabelLoading(false);
            }
            return;
        }
        if (queryTotalElements > PARCEL_SHIPPING_LABEL_EXPORT_MAX) {
            Modal.warning({
                title: t('central.parcels.exportShippingLabel.limitTitle'),
                content: t('central.parcels.exportShippingLabel.exceedsLimit', {
                    max: PARCEL_SHIPPING_LABEL_EXPORT_MAX,
                }),
            });
            return;
        }
        if (queryTotalElements === 0) {
            message.warning(t('central.parcels.exportShippingLabel.noData'));
            return;
        }
        Modal.confirm({
            title: t('central.parcels.exportShippingLabel.confirmTitle'),
            content: t('central.parcels.exportShippingLabel.confirmContent'),
            okText: t('central.parcels.exportShippingLabel.confirmOk'),
            cancelText: t('central.parcels.exportShippingLabel.confirmCancel'),
            onOk: () => {
                const exportSubmitMessageKey = 'central-parcels-export-shipping-label-submit';
                message.open({
                    key: exportSubmitMessageKey,
                    type: 'loading',
                    content: t('central.parcels.exportShippingLabel.submittingTask'),
                    duration: 0,
                });
                setExportShippingLabelLoading(true);
                void (async () => {
                    try {
                        await submitShippingLabelExportTask();
                    } catch (error: unknown) {
                        console.error('Shipping label export task failed:', error);
                        showError(error);
                    } finally {
                        message.destroy(exportSubmitMessageKey);
                        setExportShippingLabelLoading(false);
                    }
                })();
            },
        });
    };

    const handleBatchMarkOk = async (orders: BatchMarkExceptionInput[]) => {
        try {
            setBatchMarkLoading(true);
            await Promise.all(orders.map(order =>
                markAsException({
                    variables: {
                        orderId: order.orderId,
                        exceptionReasonCode: order.exceptionReasonCode,
                        exceptionHubId: order.exceptionHubId,
                        exceptionWarehouseId: order.exceptionWarehouseId,
                        exceptionImageS3KeysJson: order.exceptionImageS3KeysJson || [],
                        ...(order.exceptionSubReasons != null && order.exceptionSubReasons.length > 0 ? { exceptionSubReasons: order.exceptionSubReasons } : {})
                    },
                    refetchQueries: ['WarehouseQuery']
                })
            ))

           message.success(t('central.parcels.messages.markingExceptionSuccessfully'));
            setBatchModalVisible(false);
            handleFilter();
        } catch (error) {
            console.error('Marking exception failed:', error);
            message.error(t('central.parcels.messages.markingExceptionFailed'));
        } finally {
            setBatchMarkLoading(false);
        }
    };

    const handleSetReturnStatus = () => {
        if (selectedRows.length < 1) {
            message.warning(t('central.parcels.messages.selectAtLeastOneParcel'));
            return;
        }
        // 检查是否所有选中的包裹都是退件相关状态
        if (!selectedRows.every( row =>
            row.overallStatus === 'RETURN_ARRIVED_DESTINATION_HUB' ||
            row.overallStatus === 'RETURN_STARTED'
        )){
            message.warning(t('central.parcels.messages.selectParcelsWithReturnDirection'));
            return;
        }
        setReturnStatusVisible(true);
    };

    const handleSetReturnStatusConfirm = async (formData: SetReturnStatusData) => {
        try {
            setReturnStatusLoading(true);
            await setReturnedStatus({
                variables: {
                    orderIds: formData.orderIds,
                    returnedImageS3KeysJson: formData.returnedImageS3KeysJson
                }
            })
            message.success(t('central.parcels.messages.setReturnStatusSuccessfully'));
            setReturnStatusVisible(false);
            handleFilter();
        } catch (error) {
            console.error('set return status failed:', error);
            const { message: errorMessage } = parseException(error);
            message.error(errorMessage);
        } finally {
            setReturnStatusLoading(false);
        }
    };

    // 构建查询参数 - 与useCentralParcelQuery中的buildQueryVariables保持一致
    const buildQueryFilters = (filters: OrderFiltersType) => {
        const timeRanges = {
            orderTime: formatDateTimeRangeToISO(getEffectiveOrderTime(filters.orderTime)),
            receivedAtHub: formatDateTimeRangeToISO(filters.receivedAtHub),
            destHubArrivedAt: formatDateTimeRangeToISO(filters.destHubArrivedAt),
            destWarehouseArrivedAt: formatDateTimeRangeToISO(filters.destWarehouseArrivedAt),
            deliverAt: formatDateTimeRangeToISO(filters.deliverAt),
            markExceptionAt: formatDateTimeRangeToISO(filters.markExceptionAt),
            deliveredAt: formatDateTimeRangeToISO(filters.deliveredAt),
            lastDeliveryScanTime: formatDateTimeRangeToISO(filters.lastDeliveryScanTime),
            updatedAt: formatDateTimeRangeToISO(filters.updatedAt),
            returnArrivalHubAt: formatDateTimeRangeToISO(filters.returnArrivalHubAt)
        };

        // 处理 receiverZipcode：如果有原始输入，转换为数组（与查询列表保持一致）
        let receiverZipcode: string[] | undefined = filters.receiverZipcode;
        if (filters.receiverZipcodeInput) {
            // 支持换行符、半角逗号和空格分隔（多个空格会被视为一个分隔符）
            const zipcodes = filters.receiverZipcodeInput
                .split(/[\r\n,\s]+/)  // 按换行符、逗号或空格分割（\s+ 匹配一个或多个空白字符）
                .map(z => z.trim())
                .filter(z => z.length > 0);
            receiverZipcode = zipcodes.length > 0 ? zipcodes : undefined;
        }

        // 与查询列表保持一致的过滤项构造
        const queryFilters: any = {
            ...filters,
            ...timeRanges,
            // 转换数组类型为多值查询
            channelIds: filters.channelId?.map(id => parseInt(id)) || undefined,
            pickupHubIds: filters.pickupHubId?.map(id => parseInt(id)) || undefined,
            overallStatuses: filters.overallStatus || undefined,
            nextActions: (filters.nextActions && filters.nextActions.length > 0) ? filters.nextActions : undefined,
            deliveredDspIds: (filters.deliveredDspIds && filters.deliveredDspIds.length > 0) ? filters.deliveredDspIds : undefined,
            receiverZipcode: receiverZipcode, // 使用转换后的数组
            pickupTaskDate: undefined, // 不发送到后端
            // 转换 hubId 和 warehouseId 为数字类型（与 useCentralParcelQuery 保持一致）
            hubId: filters.hubId ? parseInt(filters.hubId) : undefined,
            warehouseId: filters.warehouseId ? parseInt(filters.warehouseId) : undefined,
            // 明确保留分拣码段字段，确保筛选条件正确传递
            sortingCodeSegment1: filters.sortingCodeSegment1 || undefined,
            sortingCodeSegment2: filters.sortingCodeSegment2 || undefined,
            sortingCodeSegment3: filters.sortingCodeSegment3 || undefined
        };

        // 清空临时输入字段，不发送到后端
        queryFilters.receiverZipcodeInput = undefined;

        // 清空单值字段，避免与多值字段冲突
        queryFilters.channelId = undefined;
        queryFilters.pickupHubId = undefined;
        queryFilters.overallStatus = undefined;
        queryFilters.nextAction = undefined;
        queryFilters.deliveredDspId = undefined;

        // 移除无效的时间范围字段，避免GraphQL schema错误
        Object.keys(queryFilters).forEach(key => {
            const value = queryFilters[key];
            if (value === undefined) {
                delete queryFilters[key];
                return;
            }
            if (typeof value === 'object' && value !== null) {
                const hasStart = Object.prototype.hasOwnProperty.call(value, 'start');
                const hasEnd = Object.prototype.hasOwnProperty.call(value, 'end');
                if ((hasStart || hasEnd) && value.start === undefined && value.end === undefined) {
                    delete queryFilters[key];
                }
            }
        });

        return queryFilters;
    };

    // Download handler function
    const handleDownloadParcels = async () => {
        const currentTime = Date.now();
        const lastTime = getLastDownloadTime();

        // Check cooldown time
        if (currentTime - lastTime < DOWNLOAD_COOLDOWN) {
            const remainingTime = Math.ceil((DOWNLOAD_COOLDOWN - (currentTime - lastTime)) / 1000);
            message.warning(`Please wait ${remainingTime} seconds before trying again to avoid duplicate requests`);
            return;
        }

        // Check if already loading
        if (downloadLoading) {
            message.warning('Report is being generated, please do not click repeatedly');
            return;
        }

        try {
            setDownloadLoading(true);
            setLastDownloadTime(currentTime); // Store to localStorage

            // 获取用户时区
            const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

            // 使用与filter按钮完全相同的参数构建逻辑
            const queryFilters = buildQueryFilters(filters);
            // 调试日志：打印构建的查询参数
            console.log('📥 [Download] 构建的查询参数:', JSON.stringify(queryFilters, null, 2));

            // 如果有选中的行，只下载选中的数据
            if (selectedRows && selectedRows.length > 0) {
                const trackingNumbers = selectedRows
                    .map(row => row.trackingNumber)
                    .filter((tn): tn is string => !!tn);

                if (trackingNumbers.length > 0) {
                    // 使用选中的 trackingNumbers 覆盖过滤条件
                    queryFilters.trackingNumbers = trackingNumbers.join(';');
                }
            }

            // 历史页走归档历史索引，普通页走实时数据源；两者签名一致，仅数据源与返回字段名不同
            const fileName = isHistoryDownload
                ? (await generateHistoryReportMutation({
                    variables: {
                        filters: historyDownloadExtraFilters ? { ...queryFilters, ...historyDownloadExtraFilters } : queryFilters,
                        timezone: userTimezone,
                        ...historyDownloadExtraVariables,
                    }
                })).data?.generateOrderHistoryDownloadReport
                : (await generateReportMutation({
                    variables: { filters: queryFilters, timezone: userTimezone }
                })).data?.generateParcelDownloadReport;
            if (fileName) {
                setGeneratedFileName(fileName);
                setDownloadCompleteModalVisible(true);
            } else {
                message.error('Failed to start report generation task');
            }
        } catch (error: any) {
            console.error('Report generation failed:', error);
            // 提取 GraphQL 错误消息
            let errorMessage = 'Report generation failed, please try again later';
            if (error?.graphQLErrors && error.graphQLErrors.length > 0) {
                const graphQLError = error.graphQLErrors[0];
                // 优先使用 extensions.message，如果没有则使用 message
                errorMessage = graphQLError.extensions?.message || graphQLError.message || errorMessage;
            } else if (error?.message) {
                errorMessage = error.message;
            }
            message.error(errorMessage);
            setDownloadLoading(false);
        }
    };

    // 原版下载处理函数
    const handleOriginalDownloadParcels = () => {
        downloadParcels(filters);
    };

    // 添加到POD人审队列处理函数
    const handleAddToPodReview = async () => {
        // 防重复点击 - 放在最前面
        if (addToPodReviewLoading) {
            return;
        }

        // 立即设置loading状态，防止重复点击
        setAddToPodReviewLoading(true);

        if (selectedRows.length < 1) {
            message.warning(t('podReview.addToPodReview.selectAtLeastOneParcel'));
            // 短暂延迟后重置loading状态
            setTimeout(() => setAddToPodReviewLoading(false), 1000);
            return;
        }

        // 检查运单状态，只有妥投状态才能添加到人审
        const nonDeliveredOrders = selectedRows.filter(row => row.overallStatus !== 'DELIVERED');
        if (nonDeliveredOrders.length > 0) {
            message.warning(t('podReview.messages.onlyDeliveredOrdersCanAddToPodReview'));
            // 短暂延迟后重置loading状态
            setTimeout(() => setAddToPodReviewLoading(false), 1000);
            return;
        }
        try {
            // 获取选中行的订单ID
            const orderIds = selectedRows.map(row => parseInt(row.id)).filter(id => !isNaN(id));

            if (orderIds.length === 0) {
                message.warning(t('podReview.addToPodReview.noValidOrderIds'));
                return;
            }

            // 调用后端API标记为待人工审核
            const {data} = await batchMarkForManualReview({
                variables: {
                    orderIds: orderIds
                },
                refetchQueries: ['CentralQuery']
            });

            if (data?.batchMarkForManualReview) {
                const result = data.batchMarkForManualReview;
                setAddToPodReviewResult(result);
                setAddToPodReviewModalVisible(true);
            } else {
                message.error(t('podReview.addToPodReview.failedToAdd'));
            }
        } catch (error: any) {
            console.error('Add to POD review error:', error);
            message.error(t('podReview.addToPodReview.failedToAdd'));
        } finally {
            setAddToPodReviewLoading(false);
        }
    };

    return (
        <div className="mb-2 pl-0 py-2 [&_.ant-select]:h-8 [&_.ant-select-selector]:h-8 [&_.ant-input:not(textarea)]:h-8 [&_.ant-picker]:h-8">
            <OrderListingQueryHint />
            {/* Base Information */}
            <div className="mb-4">
                <div className="text-sm text-gray-800 font-semibold mb-2">{t('filters.sections.baseInformation')}</div>
                <div className="bg-gray-50 rounded-md px-4 py-2">
                    {/* 使用 Grid 布局确保列对齐（Tracking 占满一行，依赖 gap-y 保持与下一行一致的间距） */}
                    <div className="grid grid-cols-3 gap-x-10 gap-y-4 items-start">
                        <div className="min-w-0 col-span-3">
                            <BatchInputFilterItem
                                label={labelTracking}
                                value={filters.trackingNumbers}
                                onChange={(e: ChangeEvent<HTMLTextAreaElement>) => setFilters({
                                    ...filters,
                                    trackingNumbers: e.target.value || undefined
                                })}
                                placeholder={t('central.parcels.placeholders.trackingNumbers') || ''}
                                spanClassName={col1SpanClassName}
                                rows={2}
                            />
                        </div>
                        <div className="min-w-0">
                <BatchInputFilterItem
                    label={labelOrder}
                    value={filters.channelOrderNumber}
                    onChange={(e: ChangeEvent<HTMLTextAreaElement>) => setFilters({...filters, channelOrderNumber: e.target.value || undefined})}
                    spanClassName={col1SpanClassName}
                    rows={1}
                />
                        </div>
                        <div className="min-w-0">
                <BatchInputFilterItem
                    label={labelExternalTracking}
                    value={filters.externalTrackingNumber}
                    onChange={(e: ChangeEvent<HTMLTextAreaElement>) => setFilters({...filters, externalTrackingNumber: e.target.value || undefined})}
                    spanClassName={getSmartLabelClassName(col2Labels)}
                    rows={1}
                />
                        </div>
                        <div className="min-w-0">
                <InputFilterItem
                    label={labelSortingChannelOrderNumber}
                    value={filters.sortingChannelOrderNumber}
                    onChange={(e) => setFilters({...filters, sortingChannelOrderNumber: e.target.value || undefined})}
                    spanClassName={getSmartLabelClassName(col3Labels)}
                />
                        </div>
                        {/* 第二行：DSP | 客户 */}
                        <div className="min-w-0">
                <DspSelectFilter
                    label={labelDsp}
                    value={filters.deliveredDspIds?.map(String)}
                    onChange={(value: string[] | undefined) => setFilters({...filters, deliveredDspIds: value?.map(v => parseInt(v)), deliveredDspId: undefined})}
                    spanClassName={col1SpanClassName}
                    placeholder={t('components.input.all') as string}
                    warehouseId={selectedWarehouseId}
                />
                        </div>
                        <div className="min-w-0">
                <MultiClientSelectFilter
                    label={labelClient}
                    value={filters.channelId}
                    onChange={(value: string[] | undefined) => setFilters({...filters, channelId: value})}
                    spanClassName={getSmartLabelClassName(col2Labels)}
                    placeholder={t('components.input.all') as string}
                />
                        </div>
                        <div className="min-w-0">
                <MultiParcelStatusSelectFilter
                    label={labelParcelStatus}
                    value={filters.overallStatus}
                    onChange={(value: string[] | undefined) => setFilters({...filters, overallStatus: value})}
                    spanClassName={getSmartLabelClassName(col3Labels)}
                    placeholder={t('components.input.all') as string}
                />
                        </div>
                </div>
                </div>
            </div>

            {/* Route Information */}
            <div className="mb-6">
                <div className="flex items-center justify-between mb-2">
                    <div className="text-sm text-gray-800 font-semibold">{t('filters.sections.routeInformation')}</div>
                </div>
                <div className="bg-gray-50 rounded-md px-4 py-2">
                    {/* 使用 Grid 布局确保列对齐 */}
                    <div className="grid grid-cols-3 gap-x-10 gap-y-4 items-start">
                        <div className="min-w-0">
                <HubSelectFilter
                    label={labelSortCode1}
                    value={filters.sortingCodeSegment1}
                    onChange={(value, hubId) => {
                        // 当清空 Hub 选择时，同时清空相关字段
                        if (!value) {
                            const newFilters = {
                                ...filters,
                                sortingCodeSegment1: undefined,
                                hubId: undefined,
                                warehouseId: undefined
                            };
                            // 同时更新 tempFilters（用于显示）和 filters（用于查询）
                            setFilters(newFilters);
                            setSelectedHubId(undefined);
                            // 直接触发查询，因为 filters 已经更新
                            guardedHandleFilter();
                        } else {
                            // 选择 Hub 时，同时更新 sortingCodeSegment1 和 hubId
                            const newFilters = {
                                ...filters,
                                sortingCodeSegment1: value,
                                hubId: hubId,
                                warehouseId: undefined // 切换 Hub 时清空 Warehouse
                            };
                            // 同时更新 tempFilters（用于显示）和 filters（用于查询）
                            setFilters(newFilters);
                            setSelectedHubId(hubId); // 保存选中的 Hub ID，用于级联过滤
                            // 直接触发查询，因为 filters 已经更新
                            guardedHandleFilter();
                        }
                    }}
                    spanClassName={col1SpanClassName}
                    placeholder={t('central.parcels.placeholders.selectHub') || 'Select HUB'}
                />
                        </div>
                        <div className="min-w-0">
                <WarehouseSelectFilter
                    label={labelSortCode2}
                    value={filters.warehouseId}
                    onChange={handleWarehouseIdChange}
                    onSelectSortingName={handleSortingNameChange}
                    onSelectSecondSegment={handleSecondSegmentChange}
                    spanClassName={getSmartLabelClassName(col2Labels)}
                    hubId={selectedHubId} // 使用 hubId 进行级联过滤（基于 parent_site_id）
                    placeholder={t('central.parcels.placeholders.selectWarehouse') || 'Select Warehouse'}
                />
                        </div>
                        <div className="min-w-0">
                <InputFilterItem
                    label={labelSortCode3}
                    value={filters.sortingCodeSegment3}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => setFilters({...filters, sortingCodeSegment3: e.target.value || undefined})}
                    spanClassName={getSmartLabelClassName(col3Labels)}
                    inputClassName="h-8"
                    inputStyle={{ paddingTop: 0, paddingBottom: 0, lineHeight: '32px' }}
                />
                        </div>
                        {/* 第二行 */}
                        <div className="min-w-0">
                <BatchInputFilterItem
                    label={labelReceiverZipcode}
                    value={filters.receiverZipcodeInput || filters.receiverZipcode?.join('\n') || ''}
                    onChange={(e: ChangeEvent<HTMLTextAreaElement>) => {
                        const value = e.target.value;
                        // 与 trackingNumbers 一致：直接保存原始字符串，在查询时转换为数组
                        setFilters({
                            ...filters,
                            receiverZipcodeInput: value || undefined
                        });
                    }}
                    placeholder={t('central.parcels.placeholders.receiverZipcode') || 'Enter multiple zipcodes, separated by line, comma or space'}
                    spanClassName={col1SpanClassName}
                    rows={1}
                />
                        </div>
                        <div className="min-w-0">
                <BagTaskNumberFilterItem
                    label={labelBagTaskNumber}
                    value={filters.bagTaskNumbers}
                    onChange={handleBagTaskNumberChange}
                    spanClassName={getSmartLabelClassName(col2Labels)}
                    placeholder={t('central.parcels.placeholders.bagTaskNumber') || 'Enter bagging task'}
                />
                        </div>
                        <div className="min-w-0">
                <MultiPickupHubSelectFilter
                    label={labelPickupHub}
                    value={filters.pickupHubId}
                    onChange={(value: string[] | undefined) => setFilters({...filters, pickupHubId: value})}
                    spanClassName={getSmartLabelClassName(col3Labels)}
                    placeholder={t('components.input.all') as string}
                />
                        </div>
                        {/* 第三行 */}
                        <div className="min-w-0">
                <PrePickupHubSelectFilter
                    label={labelPrePickupHub}
                    value={filters.prePickupHubName}
                    onChange={(name: string | undefined) => {
                        setFilters({
                            ...filters,
                            prePickupHubName: name,
                        });
                    }}
                    spanClassName={col1SpanClassName}
                    placeholder={t('central.parcels.placeholders.prePickupHub') || ''}
                />
                        </div>
                        <div className="min-w-0">
                <InputFilterItem
                    label={labelPickupSite}
                    value={filters.cooperationWarehouseId?.toString()}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => {
                        const value = e.target.value.trim();
                        const numValue = value ? parseInt(value, 10) : undefined;
                        setFilters({
                            ...filters,
                            cooperationWarehouseId: (numValue && !isNaN(numValue)) ? numValue : undefined
                        });
                    }}
                    spanClassName={getSmartLabelClassName(col2Labels)}
                    inputClassName="h-8"
                    inputStyle={{ paddingTop: 0, paddingBottom: 0, lineHeight: '32px' }}
                />
                        </div>
                        <div className="min-w-0"></div>
                    </div>
                </div>
            </div>

            {/* Date Information */}
            <div className="mb-6">
                <div className="flex items-center justify-between mb-2">
                    <div className="text-sm text-gray-800 font-semibold">{t('filters.sections.dateInformation')}</div>
                </div>
                <div className="bg-gray-50 rounded-md px-4 py-2">
                    {/* 使用 Grid 布局确保列对齐 - 二等分布局 */}
                    <div className="grid grid-cols-2 gap-x-10 gap-y-4 items-start">
                        <div className="min-w-0">
                <DateTimeRangeFilterItem
                    label={labelOrderTime}
                    value={getEffectiveOrderTime(filters.orderTime)}
                    onChange={handleOrderTimeChange}
                    spanClassName={col1SpanClassName}
                    format="YYYY/MM/DD HH:mm"
                    showTime={{
                        format: 'HH:mm',
                        defaultValue: [dayjs('00:00', 'HH:mm'), dayjs('23:59', 'HH:mm')],
                    }}
                />
                        </div>
                        <div className="min-w-0">
                <DateTimeRangeFilterItem
                    label={labelPickupTaskDate}
                    value={filters.receivedAtHub}
                    onChange={handlePickupAtChange}
                    spanClassName={col1SpanClassName}
                    format="YYYY/MM/DD HH:mm"
                    showTime={{
                        format: 'HH:mm',
                        defaultValue: [dayjs('00:00', 'HH:mm'), dayjs('23:59', 'HH:mm')],
                    }}
                />
                        </div>
                        {/* 第二行 */}
                        <div className="min-w-0">
                            {queryRangeSystem ? (
                                <DeliveredAtRangeField
                                    label={labelDeliveredAt}
                                    value={filters.deliveredAt}
                                    onChange={handleDeliveredAtChange}
                                    spanClassName={col1SpanClassName}
                                    days={queryRangeDays}
                                    system={queryRangeSystem}
                                    filters={filters}
                                />
                            ) : (
                            <DateTimeRangeFilterItem
                                label={labelDeliveredAt}
                                value={filters.deliveredAt}
                                onChange={handleDeliveredAtChange}
                                spanClassName={col1SpanClassName}
                                format="YYYY/MM/DD HH:mm"
                                showTime={{
                                    format: 'HH:mm',
                                    defaultValue: [dayjs('00:00', 'HH:mm'), dayjs('23:59', 'HH:mm')],
                                }}
                            />
                            )}
                        </div>
                        <div className="min-w-0">
                            <DateTimeRangeFilterItem
                                label={labelHubReceiveDate}
                                value={filters.destHubArrivedAt}
                                onChange={handleHubReceiveChange}
                                spanClassName={col1SpanClassName}
                                format="YYYY/MM/DD HH:mm"
                                showTime={{
                                    format: 'HH:mm',
                                    defaultValue: [dayjs('00:00', 'HH:mm'), dayjs('23:59', 'HH:mm')],
                                }}
                            />
                        </div>
                        {/* 第三行 */}
                        <div className="min-w-0">
                <DateTimeRangeFilterItem
                    label={labelWhsReceiveDate}
                    value={filters.destWarehouseArrivedAt}
                    onChange={handleWarehouseReceiveChange}
                    spanClassName={col1SpanClassName}
                    format="YYYY/MM/DD HH:mm"
                    showTime={{
                        format: 'HH:mm',
                        defaultValue: [dayjs('00:00', 'HH:mm'), dayjs('23:59', 'HH:mm')],
                    }}
                />
                        </div>
                        <div className="min-w-0">
                <DateTimeRangeFilterItem
                    label={labelLastDeliveryScanTime}
                    value={filters.lastDeliveryScanTime}
                    onChange={handleLastDeliveryScanTimeChange}
                    spanClassName={col1SpanClassName}
                    format="YYYY/MM/DD HH:mm"
                    showTime={{
                        format: 'HH:mm',
                        defaultValue: [dayjs('00:00', 'HH:mm'), dayjs('23:59', 'HH:mm')],
                    }}
                />
                        </div>
                        {/* 第四行 */}
                        <div className="min-w-0">
                <DateTimeRangeFilterItem
                    label={labelLastTaskDate}
                    value={filters.deliverAt}
                    onChange={handleDeliverTaskChange}
                    spanClassName={col1SpanClassName}
                    format="YYYY/MM/DD HH:mm"
                    showTime={{
                        format: 'HH:mm',
                        defaultValue: [dayjs('00:00', 'HH:mm'), dayjs('23:59', 'HH:mm')],
                    }}
                />
                        </div>
                        <div className="min-w-0">
                <DateTimeRangeFilterItem
                    label={labelLastUpdatedAt}
                    value={filters.updatedAt}
                    onChange={handleUpdatedAtChange}
                    spanClassName={col1SpanClassName}
                    format="YYYY/MM/DD HH:mm"
                    showTime={{
                        format: 'HH:mm',
                        defaultValue: [dayjs('00:00', 'HH:mm'), dayjs('23:59', 'HH:mm')],
                    }}
                />
                        </div>
                        <div className="min-w-0">
                            <DateTimeRangeFilterItem
                                label={labelReturnArrivalHubAt}
                                value={filters.returnArrivalHubAt}
                                onChange={handleReturnArrivalHubAtChange}
                                spanClassName={col1SpanClassName}
                                format="YYYY/MM/DD HH:mm"
                                showTime={{
                                    format: 'HH:mm',
                                    defaultValue: [dayjs('00:00', 'HH:mm'), dayjs('23:59', 'HH:mm')],
                                }}
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* Exception Information：历史包裹查询页整组隐藏（hideExceptionFilters=true）。
                异常状态来自 exception_order 表（历史链路不读）；是否投诉 / POD审核状态 / 是否有异常 /
                上次异常原因 / 上次异常时间 / 下一步动作 等其余项虽在索引里有，但按产品决策把整个
                「异常信息」分组从历史查询移除，避免残缺区块误导使用者。默认 false，原「包裹查询」页零影响。 */}
            {!hideExceptionFilters && (
            <div className="mb-6">
                <div className="flex items-center justify-between mb-2">
                    <div className="text-sm text-gray-800 font-semibold">{t('filters.sections.exceptionInformation')}</div>
                </div>
                <div className="bg-gray-50 rounded-md px-4 py-2">
                    {/* 使用 Grid 布局确保列对齐 */}
                    <div className="grid grid-cols-3 gap-x-10 gap-y-4 items-start">
                        <div className="min-w-0">
                <ParcelFilterStyle
                    label={labelIsComplaint}
                    options={isComplaintOptions}
                    value={filters.isComplaint?.toString() ?? undefined}
                    onChange={(value: string) => setFilters({...filters, isComplaint: value ? (value === 'true') : undefined})}
                    spanClassName={col1SpanClassName}
                />
                        </div>
                        <div className="min-w-0">
                <ParcelFilterStyle
                    label={labelPodReviewStatus}
                    options={podReviewStatusOptions}
                    value={filters.podReviewStatus ?? undefined}
                    onChange={(value: string) => setFilters({...filters, podReviewStatus: value || undefined})}
                    spanClassName={getSmartLabelClassName(col2Labels)}
                />
                        </div>
                        <div className="min-w-0">
                <ParcelFilterStyle
                    label={labelHasException}
                    options={hasExceptionOptions}
                    value={filters.isHasBeenSetException?.toString() ?? undefined}
                    onChange={(value: string) => setFilters({...filters, isHasBeenSetException: value ? (value === 'true') : undefined})}
                    spanClassName={getSmartLabelClassName(col3Labels)}
                />
                        </div>
                        {/* 第二行 */}
                        <div className="min-w-0">
                <ParcelFilterStyle
                    label={labelLastExceptionReason}
                    options={exceptionList.map(ex => ({
                        value: ex.exceptionReasonCode,
                        label: getExceptionReasonText(ex, ex.exceptionReasonCode)
                    })) || []}
                    value={filters.exceptionReasonCode ?? undefined}
                    onChange={(value: string) => setFilters({...filters, exceptionReasonCode: value || undefined})}
                    spanClassName={col1SpanClassName}
                />
                        </div>
                        <div className="min-w-0">
                <ParcelFilterStyle
                    label={labelExceptionStatus}
                    options={exceptionOrderStatusOptions}
                    mode="multiple"
                    maxTagCount="responsive"
                    value={filters?.exceptionStatuses?.map(String) ?? []}
                    onChange={(values: string[]) => setFilters({...filters, exceptionStatuses: values && values.length > 0 && values.length < exceptionOrderStatusOptions.length ? values.map(Number) : undefined})}
                    spanClassName={getSmartLabelClassName(col2Labels)}
                />
                        </div>
                        <div className="min-w-0">
                <ParcelFilterStyle
                    label={labelNextAction}
                    options={nextActionSelectData.filter(item => item.value !== 0).map(item => ({
                        value: item.name,
                        label: getNextActionTextFromBackend(item.name)
                    }))}
                    mode="multiple"
                    value={filters.nextActions}
                    onChange={(value: string[] | undefined) => setFilters({...filters, nextActions: value, nextAction: undefined})}
                    spanClassName={getSmartLabelClassName(col3Labels)}
                />
                        </div>
                    </div>

                    {/* 第三行（二分）：上次异常时间 */}
                    <div className="grid grid-cols-2 gap-x-10 gap-y-4 items-start mt-4">
                        <div className="min-w-0">
                            <DateTimeRangeFilterItem
                                label={labelLastExceptionTime}
                                value={filters.markExceptionAt}
                                onChange={handleExceptionTimeChange}
                                spanClassName={col1SpanClassName}
                                format="YYYY/MM/DD HH:mm"
                                showTime={{
                                    format: 'HH:mm',
                                    defaultValue: [dayjs('00:00', 'HH:mm'), dayjs('23:59', 'HH:mm')],
                                }}
                            />
                        </div>
                        <div className="min-w-0"></div>
                    </div>
                </div>
            </div>
            )}

            <div className="mb-4">
                {isSpanish ? (
                    <div className="flex flex-wrap gap-x-2 gap-y-2">
                        {!hideActionButtons && (
                        <div className="flex flex-wrap gap-2">
                            <Button
                                type="primary"
                                onClick={handlePrintLabel}
                                className="w-auto min-w-24"
                            >
                                <span className="font-bold">{t('central.parcels.buttons.printLabel')}</span>
                            </Button>
                            <Button
                                type="primary"
                                onClick={handleExportShippingLabel}
                                loading={exportShippingLabelLoading}
                                className="w-auto min-w-24"
                            >
                                <span className="font-bold">{t('central.parcels.buttons.exportShippingLabel')}</span>
                            </Button>
                            <Button 
                                type="primary" 
                                onClick={handleMarkAsException}
                                className="w-auto min-w-24"
                            >
                                <span className="font-bold">{t('central.parcels.buttons.markAsException')}</span>
                            </Button>
                            <Button 
                                type="primary" 
                                onClick={handleSetReturnStatus}
                                className="w-auto min-w-24"
                            >
                                <span className="font-bold">{t('central.parcels.buttons.setReturnStatus')}</span>
                            </Button>
                            <Button
                                type="primary"
                                onClick={handleAddToPodReview}
                                loading={addToPodReviewLoading}
                                className="w-auto min-w-24"
                            >
                                <span className="font-bold">{t('podReview.addToPodReview.title')}</span>
                            </Button>
                            {hasSetDeliverStatusPermission && (
                                <Button
                                    type="primary"
                                    onClick={() => handleSetDeliverStatus(selectedRows)}
                                    className="w-auto min-w-24"
                                >
                                    <span className="font-bold">{t('central.parcels.buttons.setDeliverStatus')}</span>
                                </Button>
                            )}
                        </div>
                        )}
                        <div className="flex flex-wrap gap-2 ml-auto">
                            <Button
                                type="primary"
                                onClick={copyTrackingNumbers}
                                className="w-auto min-w-24"
                            >
                                <span className="font-bold">{t('common.buttons.copyTracking')}</span>
                            </Button>
                            <Button
                                type="primary"
                                onClick={handleDownloadParcels}
                                loading={cooldownRemaining > 0}
                                disabled={downloadLoading || cooldownRemaining > 0}
                                className="w-auto min-w-24"
                            >
                                <span className="font-bold">
                                    {cooldownRemaining > 0
                                        ? `Download ${cooldownRemaining}s`
                                        : t('components.button.download')
                                    }
                                </span>
                            </Button>
                            <Button type="primary" onClick={guardedHandleFilter} disabled={deliveredAtBlocked} className="w-auto min-w-24">
                                <span className="font-bold">{t('components.button.filter')}</span>
                            </Button>
                            <Button onClick={handleResetFiltersWithDefaultTime} className="w-auto min-w-24">
                                <span className="font-bold">{t('components.button.reset')}</span>
                            </Button>
                        </div>
                    </div>
                ) : (
                    <div className="flex flex-wrap gap-x-2 gap-y-4">
                        {!hideActionButtons && (
                        <div className="flex flex-wrap gap-2">
                            <Button type="primary" onClick={handlePrintLabel} className="w-auto min-w-24">
                                <span className="font-bold">{t('central.parcels.buttons.printLabel')}</span>
                            </Button>
                            <Button
                                type="primary"
                                onClick={handleExportShippingLabel}
                                loading={exportShippingLabelLoading}
                                className="w-auto min-w-24"
                            >
                                <span className="font-bold">{t('central.parcels.buttons.exportShippingLabel')}</span>
                            </Button>
                            <Button type="primary" onClick={handleMarkAsException} className="w-auto min-w-24">
                                <span className="font-bold">{t('central.parcels.buttons.markAsException')}</span>
                            </Button>
                            <Button type="primary" onClick={handleSetReturnStatus} className="w-auto min-w-24">
                                <span className="font-bold">{t('central.parcels.buttons.setReturnStatus')}</span>
                            </Button>
                            <Button
                                type="primary"
                                onClick={handleAddToPodReview}
                                loading={addToPodReviewLoading}
                                className="w-auto min-w-24"
                            >
                                <span className="font-bold">{t('podReview.addToPodReview.title')}</span>
                            </Button>
                            {hasSetDeliverStatusPermission && (
                                <Button type="primary" onClick={() => handleSetDeliverStatus(selectedRows)} className="w-auto min-w-24">
                                    <span className="font-bold">{t('central.parcels.buttons.setDeliverStatus')}</span>
                                </Button>
                            )}
                        </div>
                        )}

                        <div className="flex flex-wrap gap-2 ml-auto">
                            <Button
                                type="primary"
                                onClick={copyTrackingNumbers}
                                className="w-auto min-w-24"
                            >
                                <span className="font-bold">{t('common.buttons.copyTracking')}</span>
                            </Button>
                            <Button
                                type="primary"
                                onClick={handleDownloadParcels}
                                loading={cooldownRemaining > 0}
                                disabled={downloadLoading || cooldownRemaining > 0}
                                className="w-auto min-w-24"
                            >
                                <span className="font-bold">
                                    {cooldownRemaining > 0
                                        ? `Download ${cooldownRemaining}s`
                                        : t('components.button.download')
                                    }
                                </span>
                            </Button>
                            <Button type="primary" onClick={guardedHandleFilter} disabled={deliveredAtBlocked} className="w-auto min-w-24">
                                <span className="font-bold">{t('components.button.filter')}</span>
                            </Button>
                            <Button onClick={handleResetFiltersWithDefaultTime} className="w-auto min-w-24">
                                <span className="font-bold">{t('components.button.reset')}</span>
                            </Button>
                        </div>
                    </div>
                )}
            </div>

            {/* 批量标记异常弹窗 */}
            <BatchMarkAsExceptionModal
                    visible={batchModalVisible}
                    parcels={selectedRows.map(row => ({
                        orderId: Number(row.id), 
                        trackingNumber: row.trackingNumber,
                        existingImageUrls: row.exceptionImageS3UrlsJson || [],
                        exceptionHubId: row.exceptionHubId,
                        exceptionWarehouseId: row.exceptionWarehouseId
                    }))}
                    onOk={handleBatchMarkOk}
                    onCancel={() => setBatchModalVisible(false)}
                    loading={batchMarkLoading}
                    userRole={userRole}
                    exceptionSelectData={exceptionSelectData}
                />
                
            <SetReturnStatusModal
                visible={returnStatusVisible}
                onClose={() => setReturnStatusVisible(false)}
                onConfirm={handleSetReturnStatusConfirm}
                loading={returnStatusLoading}
                parcels={selectedRows}
            />
            <SetDeliverStatusModal
                visible={deliverStatusVisible}
                onClose={() => setDeliverStatusVisible(false)}
                onConfirm={handleSetDeliverStatusConfirm}
                loading={deliverStatusLoading}
                parcels={filteredDeliverRows}
            />
            <FailedOrdersModal
                visible={failedOrdersVisible}
                failedOrders={failedOrders}
                onClose={() => {
                    setFailedOrdersVisible(false);
                    setFailedOrders([]);
                }}
            />
            <ParcelsDownloadModal
                visible={downloadCompleteModalVisible}
                onClose={() => setDownloadCompleteModalVisible(false)}
                fileName={generatedFileName}
            />
            <ShippingLabelExportSuccessModal
                visible={shippingLabelExportSuccessModalVisible}
                onClose={() => setShippingLabelExportSuccessModalVisible(false)}
                fileName={shippingLabelExportFileName}
            />

            <AddToPodReviewModal
                visible={addToPodReviewModalVisible}
                onClose={() => setAddToPodReviewModalVisible(false)}
                result={addToPodReviewResult}
            />
        </div>
    );
};

export default CentralParcelFilter;